import { useRef } from 'react';
import { templates as allTemplates, Template } from '../../../mocks/websiteTemplates';
import { COLLECTIONS, Collection } from '../websiteBuilderCollections';

interface TemplateCollectionsProps {
  activeCollectionId: string | null;
  onSelectCollection: (id: string | null, templates: Template[]) => void;
}

function CollectionCard({
  collection,
  isActive,
  count,
  previews,
  onSelect,
}: {
  collection: Collection;
  isActive: boolean;
  count: number;
  previews: Template[];
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex-shrink-0 w-56 rounded-2xl overflow-hidden text-left transition-all duration-200 cursor-pointer group ${
        isActive ? 'ring-2 ring-violet-500 ring-offset-2 scale-[1.02]' : 'hover:scale-[1.02]'
      }`}
    >
      {/* Card body with gradient */}
      <div className={`relative bg-gradient-to-br ${collection.gradient} p-4 pb-3`} style={{ minHeight: 160 }}>
        {/* Background micro thumbnails — decorative */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-20">
          {previews.slice(0, 3).map((t, i) => (
            <div
              key={t.name}
              className="absolute"
              style={{
                right: i * 20 - 10,
                top: i * 18,
                width: 100,
                height: 70,
                transform: `rotate(${(i - 1) * 6}deg)`,
              }}
            >
              <img src={t.image} alt="" className="w-full h-full object-cover rounded-lg" />
            </div>
          ))}
        </div>

        {/* Icon */}
        <div className="relative z-10 w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3 border border-white/30">
          <i className={`${collection.icon} text-white text-lg`}></i>
        </div>

        {/* Name + tagline */}
        <div className="relative z-10">
          <h3 className="text-white font-bold text-sm leading-tight">{collection.name}</h3>
          <p className="text-white/70 text-xs mt-0.5 leading-tight">{collection.tagline}</p>
        </div>

        {/* Count badge */}
        <div className="absolute top-3 right-3 z-10 px-2 py-0.5 bg-black/30 backdrop-blur-sm text-white text-xs font-semibold rounded-full border border-white/20">
          {count}
        </div>

        {/* Active indicator */}
        {isActive && (
          <div className="absolute bottom-3 right-3 z-10 w-6 h-6 rounded-full bg-white flex items-center justify-center">
            <i className="ri-check-line text-gray-900 text-sm"></i>
          </div>
        )}
      </div>

      {/* Mini preview strip */}
      <div className="flex gap-0.5 bg-gray-100">
        {previews.slice(0, 3).map((t) => (
          <div key={t.name} className="flex-1" style={{ height: 36 }}>
            <img src={t.image} alt="" className="w-full h-full object-cover object-top" />
          </div>
        ))}
      </div>

      {/* Explore label */}
      <div className="bg-white border-t border-gray-100 px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700 group-hover:text-violet-600 transition-colors">
          {isActive ? 'Viewing' : 'Explore'}
        </span>
        <i className={`ri-arrow-right-line text-xs transition-colors ${isActive ? 'text-violet-500' : 'text-gray-400 group-hover:text-violet-500'}`}></i>
      </div>
    </button>
  );
}

export default function TemplateCollections({
  activeCollectionId,
  onSelectCollection,
}: TemplateCollectionsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  };

  return (
    <section data-tour="collections" className="mb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <div className="w-1 h-4 rounded-full bg-gradient-to-b from-violet-500 to-cyan-500"></div>
            <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">Curated</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Collections</h2>
          <p className="text-xs text-gray-500 mt-0.5">Hand-picked groups based on style &amp; purpose</p>
        </div>
        <div className="flex items-center gap-2">
          {activeCollectionId && (
            <button
              onClick={() => onSelectCollection(null, [])}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
              Clear
            </button>
          )}
          <button onClick={() => scroll('left')} className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button onClick={() => scroll('right')} className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer">
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Horizontal scroll */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {COLLECTIONS.map((col) => {
          const matching = allTemplates.filter(col.filterFn);
          return (
            <CollectionCard
              key={col.id}
              collection={col}
              isActive={activeCollectionId === col.id}
              count={matching.length}
              previews={matching.slice(0, 3)}
              onSelect={() => {
                if (activeCollectionId === col.id) {
                  onSelectCollection(null, []);
                } else {
                  onSelectCollection(col.id, matching);
                }
              }}
            />
          );
        })}
      </div>
    </section>
  );
}
